/**
 * NavLog Generator — produces per-leg navigation data with wind corrections,
 * fuel burn, and cumulative totals. Platform-agnostic.
 */
import type { Route, NavigationLog, NavLogLeg, LegFrequency, AircraftPerformance, WindData } from './types';
import { RouteEngine } from './route-engine';

export class NavLogGenerator {
  private routeEngine: RouteEngine;

  constructor(routeEngine?: RouteEngine) {
    this.routeEngine = routeEngine ?? new RouteEngine();
  }

  async generateNavLog(
    route: Route,
    aircraft: AircraftPerformance,
    winds?: WindData[],
    frequencyLookup?: (waypointId: string) => Promise<LegFrequency[]>,
  ): Promise<NavigationLog> {
    const navLegs: NavLogLeg[] = [];
    let cumulativeDistanceNm = 0;
    let cumulativeFuelGallons = 0;

    for (let i = 0; i < route.legs.length; i++) {
      const leg = route.legs[i];
      const wind = winds && winds[i] ? winds[i] : undefined;
      const metrics = this.routeEngine.computeLegMetrics(leg.from, leg.to, aircraft.cruiseTas, wind);

      const fuelBurnGallons = (metrics.estimatedTimeMinutes / 60) * aircraft.fuelBurnGph;
      cumulativeDistanceNm += metrics.distanceNm;
      cumulativeFuelGallons += fuelBurnGallons;

      const windCorrectedHeading = this.computeWindCorrectedHeading(
        metrics.trueCourse, wind, aircraft.cruiseTas,
      );

      const frequencies = frequencyLookup
        ? await frequencyLookup(leg.to.identifier)
        : [];

      navLegs.push({
        fromWaypoint: leg.from.identifier,
        toWaypoint: leg.to.identifier,
        trueCourse: metrics.trueCourse,
        magneticCourse: metrics.magneticCourse,
        windCorrectedHeading,
        groundSpeed: metrics.groundSpeed ?? aircraft.cruiseTas,
        distanceNm: metrics.distanceNm,
        estimatedTimeMinutes: metrics.estimatedTimeMinutes,
        fuelBurnGallons,
        cumulativeDistanceNm,
        cumulativeFuelGallons,
        plannedAltitude: leg.plannedAltitude,
        frequencies,
      });
    }

    const totalFuelGallons = navLegs.reduce((s, l) => s + l.fuelBurnGallons, 0);

    return {
      legs: navLegs,
      totalDistanceNm: route.totalDistanceNm,
      totalTimeMinutes: navLegs.reduce((s, l) => s + l.estimatedTimeMinutes, 0),
      totalFuelGallons,
      aircraftId: aircraft.profileId,
      generatedAt: Date.now(),
    };
  }

  private computeWindCorrectedHeading(
    trueCourse: number, wind: WindData | undefined, tas: number,
  ): number {
    if (!wind || wind.speed === 0) return trueCourse;
    const DEG_TO_RAD = Math.PI / 180;
    const RAD_TO_DEG = 180 / Math.PI;
    const wca = Math.asin(
      (wind.speed * Math.sin(DEG_TO_RAD * (wind.direction - trueCourse))) / tas,
    ) * RAD_TO_DEG;
    return ((trueCourse + wca) % 360 + 360) % 360;
  }
}
